'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useState, useEffect } from 'react'
import {
  Calendar,
  PartyPopper,
  Users,
  UserCog,
  UtensilsCrossed,
  Bell,
  LayoutDashboard,
  Menu,
  LogOut,
  Settings,
  Sparkles,
  FileText,
  Shield,
  FileCheck,
} from 'lucide-react'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarLink,
} from '@planner/ui'
import { useAuth } from '@/contexts/AuthContext'
import { useNotifications } from '@/features/notifications/hooks/useNotifications'
import { Button } from '@planner/ui'
import { Avatar, AvatarFallback, AvatarImage } from '@planner/ui'
import { Sheet, SheetContent, SheetTrigger } from '@planner/ui'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@planner/ui'

interface NavItem {
  title: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  roles?: ('admin' | 'pracownik' | 'klient')[]
  superadminOnly?: boolean
}

const navItems: NavItem[] = [
  {
    title: 'Dashboard',
    href: '/dashboard',
    icon: LayoutDashboard,
  },
  {
    title: 'Kalendarz',
    href: '/calendar',
    icon: Calendar,
    roles: ['admin', 'pracownik'],
  },
  {
    title: 'Kalendarz',
    href: '/calendar/public',
    icon: Calendar,
    roles: ['klient'],
  },
  {
    title: 'Wydarzenia',
    href: '/events',
    icon: PartyPopper,
  },
  {
    title: 'Klienci',
    href: '/clients',
    icon: Users,
    roles: ['admin', 'pracownik'],
  },
  {
    title: 'Zespół',
    href: '/settings/team',
    icon: UserCog,
    roles: ['admin'],
  },
  {
    title: 'Menu',
    href: '/menu',
    icon: UtensilsCrossed,
    roles: ['admin', 'pracownik'],
  },
  {
    title: 'Usługi dodatkowe',
    href: '/services',
    icon: Sparkles,
    roles: ['admin', 'pracownik'],
  },
  {
    title: 'Dokumenty',
    href: '/documents',
    icon: FileCheck,
    roles: ['admin', 'pracownik'],
  },
  {
    title: 'Raporty',
    href: '/reports',
    icon: FileText,
    roles: ['admin', 'pracownik'],
  },
  {
    title: 'Powiadomienia',
    href: '/notifications',
    icon: Bell,
  },
  {
    title: 'Admin platformy',
    href: '/platform-admin',
    icon: Shield,
    superadminOnly: true,
  },
]

export function AppSidebar() {
  const pathname = usePathname()
  const { user, userRole, signOut, isSuperadmin } = useAuth()
  const { unreadCount } = useNotifications()
  const [mounted, setMounted] = useState(false)

  // Prevent hydration mismatch by only filtering on client
  useEffect(() => {
    setMounted(true)
  }, [])

  const filteredNavItems = mounted
    ? navItems.filter((item) => {
        if (item.superadminOnly && !isSuperadmin) return false
        if (!item.roles) return true
        return userRole ? item.roles.includes(userRole) : false
      })
    : navItems // Show all items during SSR to prevent mismatch

  const getInitials = (email: string) => {
    return email
      .split('@')[0]
      .split('.')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  // Calculate initials only after mount to prevent hydration mismatch
  const userInitials = mounted && user?.email ? getInitials(user.email) : 'U'

  return (
    <>
      {/* Desktop Sidebar */}
      <Sidebar className="hidden md:flex">
        <SidebarHeader>
          <Link href="/dashboard" className="flex items-center gap-2 px-2 cursor-pointer">
            <img 
              src="/logo%20banquieto.png" 
              alt="Banquieto" 
              className="h-8 w-auto"
            />
          </Link>
        </SidebarHeader>
        <SidebarContent>
          <nav className="space-y-1">
            {filteredNavItems.map((item) => {
              const Icon = item.icon
              const isActive = pathname === item.href
              return (
                <SidebarLink
                  key={item.href}
                  href={item.href}
                  active={isActive}
                  asChild
                >
                  <Link href={item.href} className="flex items-center gap-3 relative">
                    <Icon className="h-5 w-5" />
                    <span>{item.title}</span>
                    {item.href === '/notifications' && unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-semibold">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </Link>
                </SidebarLink>
              )
            })}
          </nav>
        </SidebarContent>
        <SidebarFooter className="border-t p-4">
          <div className="space-y-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex items-center gap-3 w-full justify-start p-2 h-auto hover:bg-accent"
                >
                  <Avatar className="h-9 w-9">
                    <AvatarImage src={user?.user_metadata?.avatar_url} />
                    <AvatarFallback className="text-xs">
                      {userInitials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col items-start flex-1 min-w-0">
                    <span className="text-sm font-medium truncate w-full">
                      {user?.email || 'Użytkownik'}
                    </span>
                    <span className="text-xs text-muted-foreground truncate w-full">
                      {isSuperadmin
                        ? 'super admin'
                        : userRole || 'Brak roli'}
                    </span>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 shadow-lg">
                <DropdownMenuLabel>Moje konto</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link href="/settings" className="flex items-center gap-2 cursor-pointer">
                    <Settings className="h-4 w-4" />
                    Ustawienia
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={signOut} className="text-destructive cursor-pointer">
                  <LogOut className="h-4 w-4 mr-2" />
                  Wyloguj się
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </SidebarFooter>
      </Sidebar>

      {/* Mobile Sidebar */}
      <Sheet>
        <SheetTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden fixed top-2 left-2 z-50 bg-background/80 backdrop-blur-md border shadow-lg hover:shadow-xl transition-all"
          >
            <Menu className="h-5 w-5" />
            <span className="sr-only">Toggle menu</span>
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-64 p-0">
          <div className="flex h-full flex-col">
            <div className="flex h-16 items-center border-b px-6">
              <Link href="/dashboard" className="flex items-center gap-2 cursor-pointer">
                <img 
                  src="/logo%20banquieto.png" 
                  alt="Banquieto" 
                  className="h-8 w-auto"
                />
              </Link>
            </div>
            <div className="flex-1 overflow-y-auto px-3 py-4">
              <nav className="space-y-1">
                {filteredNavItems.map((item) => {
                  const Icon = item.icon
                  const isActive = pathname === item.href
                  return (
                    <Link
                      key={item.href}
                      href={item.href}
                      className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all relative ${
                        isActive
                          ? 'bg-primary/10 text-primary font-semibold shadow-sm border-l-2 border-l-primary before:absolute before:inset-0 before:bg-gradient-to-r before:from-orange-50/30 before:to-transparent dark:before:from-orange-950/10 before:pointer-events-none'
                          : 'hover:bg-primary/10 hover:text-primary hover:shadow-sm hover:border-l-2 hover:border-l-primary/30'
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                      <span>{item.title}</span>
                      {item.href === '/notifications' && unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-semibold">
                          {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                      )}
                    </Link>
                  )
                })}
              </nav>
            </div>
            <div className="border-t px-6 py-4 space-y-3">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex items-center gap-3 w-full justify-start p-2 h-auto hover:bg-accent"
                  >
                    <Avatar className="h-9 w-9">
                      <AvatarImage src={user?.user_metadata?.avatar_url} />
                      <AvatarFallback className="text-xs">
                        {user?.email ? getInitials(user.email) : 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col items-start flex-1 min-w-0">
                      <span className="text-sm font-medium truncate w-full">
                        {user?.email || 'Użytkownik'}
                      </span>
                      <span className="text-xs text-muted-foreground truncate w-full">
                        {isSuperadmin
                          ? 'super admin'
                          : userRole || 'Brak roli'}
                      </span>
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 shadow-lg">
                  <DropdownMenuLabel>Moje konto</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link href="/settings" className="flex items-center gap-2 cursor-pointer">
                      <Settings className="h-4 w-4" />
                      Ustawienia
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={signOut} className="text-destructive cursor-pointer">
                    <LogOut className="h-4 w-4 mr-2" />
                    Wyloguj się
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  )
}

